import type { ServiceLabels } from '@/lib/form-state/util'
import { apiRequest, ApiResponse } from '../request'

export enum SearchModule {
    ATENDIMENTO = 'atendimento',
    PRODUTO = 'produto',
    SETOR = 'setor',
    ATENDENTE = 'atendente',
    AGENDA = 'agenda',
    CONSULTA = 'consulta',
    TRIAGEM = 'triagem',
    EXAME = 'exame',
    RECEITA = 'receita',
    PROCEDIMENTO = 'procedimento',
    CLIENTE = 'cliente',
    PROFISSIONAL = 'profissional',
    CATALOGO_PROCEDIMENTO = 'catalogo_procedimento',
    SALA = 'sala',
    LOTE_FATURAMENTO = 'lote_faturamento',
    TABELA_PRECO = 'tabela_preco',
    GUIA_TISS = 'guia_tiss',
    PAGAMENTO = 'pagamento',
    MEDICAMENTO = 'medicamento',
    DISPENSACAO = 'dispensacao',
    TEMPERATURA = 'temperatura',
    LEITO = 'leito',
    UNIDADE_HOSPITALAR = 'unidade_hospitalar',
    INTERNACAO = 'internacao',
    HIGIENIZACAO = 'higienizacao',
    SINAIS_VITAIS = 'sinais_vitais',
    BALANCO_HIDRICO = 'balanco_hidrico',
    PRESCRICAO_HOSPITALAR = 'prescricao_hospitalar',
    ALTA_HOSPITALAR = 'alta_hospitalar',
}

export interface SearchResultItem {
    id: number
    module: SearchModule
    moduleLabel: string
    title: string
    subtitle?: string
    description?: string
    detailUrl: string
    icon: string
    relevance: number
    createdAt?: string
    updatedAt?: string
}

export interface GlobalSearchResult {
    query: string
    totalResults: number
    results: SearchResultItem[]
    resultsByModule: Partial<Record<SearchModule, SearchResultItem[]>>
}

export interface GlobalSearchParams {
    query: string
    modules?: SearchModule[]
    limit?: number
    fullSearch?: boolean
}

export const MODULE_CONFIG: Record<SearchModule, { label: string; icon: string; color: string }> = {
    [SearchModule.ATENDIMENTO]: { label: 'Atendimentos', icon: 'MdChat', color: 'badge-secondary' },
    [SearchModule.PRODUTO]: { label: 'Produtos/Servicos', icon: 'MdInventory', color: 'badge-info' },
    [SearchModule.SETOR]: { label: 'Setores', icon: 'MdBusiness', color: 'badge-secondary' },
    [SearchModule.ATENDENTE]: { label: 'Atendentes', icon: 'MdSupportAgent', color: 'badge-accent' },
    [SearchModule.AGENDA]: { label: 'Agenda', icon: 'MdCalendarToday', color: 'badge-warning' },
    [SearchModule.CONSULTA]: { label: 'Consultas', icon: 'MdLocalHospital', color: 'badge-error' },
    [SearchModule.TRIAGEM]: { label: 'Triagens', icon: 'MdAssignment', color: 'badge-secondary' },
    [SearchModule.EXAME]: { label: 'Exames', icon: 'MdBiotech', color: 'badge-success' },
    [SearchModule.RECEITA]: { label: 'Receituario', icon: 'MdDescription', color: 'badge-info' },
    [SearchModule.PROCEDIMENTO]: { label: 'Procedimentos', icon: 'MdMedicalServices', color: 'badge-secondary' },
    [SearchModule.CLIENTE]: { label: 'Clientes/Pacientes', icon: 'MdPerson', color: 'badge-accent' },
    [SearchModule.PROFISSIONAL]: { label: 'Profissionais', icon: 'MdBadge', color: 'badge-warning' },
    [SearchModule.CATALOGO_PROCEDIMENTO]: { label: 'Catálogo de Procedimentos', icon: 'MdList', color: 'badge-error' },
    [SearchModule.SALA]: { label: 'Salas/Consultorios', icon: 'MdMeetingRoom', color: 'badge-secondary' },
    [SearchModule.LOTE_FATURAMENTO]: { label: 'Lotes de Faturamento', icon: 'MdFolderOpen', color: 'badge-success' },
    [SearchModule.TABELA_PRECO]: { label: 'Tabelas de Precos', icon: 'MdPriceCheck', color: 'badge-info' },
    [SearchModule.GUIA_TISS]: { label: 'Guias TISS', icon: 'MdReceipt', color: 'badge-secondary' },
    [SearchModule.PAGAMENTO]: { label: 'Pagamentos', icon: 'MdPayment', color: 'badge-accent' },
    [SearchModule.MEDICAMENTO]: { label: 'Medicamentos', icon: 'MdMedication', color: 'badge-warning' },
    [SearchModule.DISPENSACAO]: { label: 'Dispensacoes', icon: 'MdLocalPharmacy', color: 'badge-error' },
    [SearchModule.TEMPERATURA]: { label: 'Temperatura', icon: 'MdThermostat', color: 'badge-secondary' },
    [SearchModule.LEITO]: { label: 'Leitos', icon: 'MdBed', color: 'badge-success' },
    [SearchModule.UNIDADE_HOSPITALAR]: { label: 'Unidades Hospitalares', icon: 'MdApartment', color: 'badge-info' },
    [SearchModule.INTERNACAO]: { label: 'Internacoes', icon: 'MdHotel', color: 'badge-secondary' },
    [SearchModule.HIGIENIZACAO]: { label: 'Higienizacao', icon: 'MdCleaningServices', color: 'badge-accent' },
    [SearchModule.SINAIS_VITAIS]: { label: 'Sinais Vitais', icon: 'MdMonitorHeart', color: 'badge-warning' },
    [SearchModule.BALANCO_HIDRICO]: { label: 'Balanco Hidrico', icon: 'MdWaterDrop', color: 'badge-error' },
    [SearchModule.PRESCRICAO_HOSPITALAR]: { label: 'Prescricoes Hospitalares', icon: 'MdEditNote', color: 'badge-secondary' },
    [SearchModule.ALTA_HOSPITALAR]: { label: 'Altas Hospitalares', icon: 'MdExitToApp', color: 'badge-success' },
}

/**
 * `MODULE_CONFIG` no vocabulário do segmento: fora da saúde, Procedimento vira
 * Pedido. Só os módulos de atendimento mudam; o resto vem intacto.
 */
export function getModuleConfig(
    labels: ServiceLabels
): typeof MODULE_CONFIG {
    return {
        ...MODULE_CONFIG,
        [SearchModule.CONSULTA]: {
            ...MODULE_CONFIG[SearchModule.CONSULTA],
            label: labels.Consultations,
        },
        [SearchModule.EXAME]: {
            ...MODULE_CONFIG[SearchModule.EXAME],
            label: labels.Exams,
        },
        [SearchModule.PROCEDIMENTO]: {
            ...MODULE_CONFIG[SearchModule.PROCEDIMENTO],
            label: labels.Orders,
        },
        [SearchModule.CATALOGO_PROCEDIMENTO]: {
            ...MODULE_CONFIG[SearchModule.CATALOGO_PROCEDIMENTO],
            label: `Catálogo de ${labels.Orders}`,
        },
    }
}

export async function searchGlobal(
    params: GlobalSearchParams
): Promise<ApiResponse<GlobalSearchResult>> {
    return apiRequest<GlobalSearchResult>(
        'global-search',
        { params },
        'get'
    )
}

/* ==================== BUSCA POR NÚMERO (id do documento) ==================== */

export type NumberSearchType =
    | 'EXAM'
    | 'PROCEDURE'
    | 'CONSULTATION'
    | 'BILLING'

export interface NumberSearchHit {
    type: NumberSearchType
    typeLabel: string
    id: number
    title: string
    clientName: string | null
    professionalName: string | null
    partnerName: string | null
    statusLabel: string | null
    date: string | null
    /** Centavos. */
    amount: number | null
    /** Rota da tela de detalhe. */
    url: string
}

export interface NumberSearchResult {
    number: number
    total: number
    hits: NumberSearchHit[]
}

/**
 * Busca o MESMO número em exame, procedimento, consulta e cobrança.
 *
 * Os ids colidem entre as tabelas (o número 100 existe nas quatro), então é normal
 * voltar mais de um resultado — quem escolhe é o operador.
 */
export async function searchByNumber(
    numberValue: number,
    /**
     * `organization` procura na organização inteira em vez de só na filial do
     * operador. Usado pelo vínculo de cobrança com atendimento, que atravessa
     * filiais; a busca por número do menu segue no padrão (`company`).
     */
    scope?: 'company' | 'organization'
): Promise<ApiResponse<NumberSearchResult>> {
    return apiRequest<NumberSearchResult>(
        'global-search/by-number',
        { params: { number: numberValue, ...(scope ? { scope } : {}) } },
        'get'
    )
}

export interface ClientSearchResult {
    hits: NumberSearchHit[]
}

/**
 * Busca os documentos pelo NOME DO PACIENTE — o outro lado da busca por número.
 *
 * Quem liga sem o número ("sou a Maria, marquei uns exames") não tinha por onde ser
 * atendido. Devolve documentos, não pacientes: com homônimos, os de todos entram na
 * mesma lista, e o nome em cada linha é o que desambigua.
 */
export async function searchByClient(
    query: string
): Promise<ApiResponse<ClientSearchResult>> {
    return apiRequest<ClientSearchResult>(
        'global-search/by-client',
        { params: { query } },
        'get'
    )
}
